/**
 * ExtractedDataCard — read-only view of the Capa 0 extraction
 * (`FinalDecisionCompact.extracted_fields`, serialized verbatim from
 * `contracts/extraction.py`; `{}` when intake failed before reading the docs).
 *
 * Wire shape:
 *   ine_front:   { curp/nombre: {value, legible}, vigencia_inicio/fin: int|null,
 *                  document_present: bool }
 *   ine_back:    null | { mrz_lines: string[], cic: {value, legible},
 *                  document_present: bool }
 *   certificado: { curp/folio/nombre/institucion: {value, legible},
 *                  anio_emision: int|null, has_qr, qr_payload, sep_seal_present,
 *                  cert_type, legible,
 *                  conformidad?: {tipo_documento, titulo_literal, documento_completo,
 *                    escudo_nacional, logo_institucional, sellos_oficiales,
 *                    firmas_o_sello_digital, foto_alumno, fondo_seguridad,
 *                    forma_captura, anomalias, color, color_folio, sello_foto,
 *                    estado_qr} }
 *                  (ADR 0012 — each field only when the model made that
 *                  observation; see certConformidad.ts for the rendered rows)
 */

import type { ReactNode } from "react";
import { Box, Card, CardContent, Divider, Stack, Typography } from "@mui/material";

import { CERT_TYPE_LABEL } from "../../api/labels";
import { conformidadRows } from "./certConformidad";

/** A rendered cell: the display text plus whether it is a muted placeholder. */
type RenderedField = { text: string; muted: boolean };

const NOT_READ: RenderedField = { text: "no leído", muted: true };
const ILLEGIBLE: RenderedField = { text: "ilegible", muted: true };

/** Narrow an unknown value to a plain object (not null, not an array). */
export function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** A `{value, legible}` text field: show value, else "ilegible"/"no leído". */
function renderTextField(field: unknown): RenderedField {
  const rec = asRecord(field);
  if (!rec) return NOT_READ;
  if (rec.legible === false) return ILLEGIBLE;
  const value = rec.value;
  if (typeof value === "string" && value.trim() !== "") {
    return { text: value, muted: false };
  }
  return NOT_READ;
}

/** A plain string field (e.g. qr_payload). */
function renderString(value: unknown): RenderedField {
  if (typeof value === "string" && value.trim() !== "") {
    return { text: value, muted: false };
  }
  return NOT_READ;
}

/** A nullable integer field (vigencia_inicio/fin, anio_emision). */
function renderInt(value: unknown): RenderedField {
  if (typeof value === "number" && Number.isFinite(value)) {
    return { text: String(value), muted: false };
  }
  return NOT_READ;
}

/** A boolean field (has_qr, sep_seal_present, document_present, legible). */
function renderBool(value: unknown): RenderedField {
  if (typeof value === "boolean") {
    return { text: value ? "Sí" : "No", muted: false };
  }
  return NOT_READ;
}

export function FieldRow({
  label,
  text,
  muted,
  numeric = false,
}: {
  label: string;
  text: string;
  muted: boolean;
  numeric?: boolean;
}) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        color={muted ? "text.secondary" : "text.primary"}
        sx={{
          fontVariantNumeric: numeric ? "tabular-nums" : undefined,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

function DocGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Stack spacing={0.5}>{children}</Stack>
    </Box>
  );
}

export function ExtractedDataCard({ fields }: { fields: Record<string, unknown> }) {
  const hasData = !!fields && Object.keys(fields).length > 0;
  const ineFront = hasData ? asRecord(fields.ine_front) : null;
  const ineBack = hasData ? asRecord(fields.ine_back) : null;
  const cert = hasData ? asRecord(fields.certificado) : null;

  const mrzLines = Array.isArray(ineBack?.mrz_lines) ? ineBack.mrz_lines.length : 0;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h3" gutterBottom>
          Datos extraídos
        </Typography>

        {!hasData ? (
          <Typography variant="body2" color="text.secondary">
            Sin extracción — la ingesta falló antes de leer los documentos.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <DocGroup title="INE (frente)">
              {ineFront ? (
                <>
                  <FieldRow label="CURP" {...renderTextField(ineFront.curp)} />
                  <FieldRow label="Nombre" {...renderTextField(ineFront.nombre)} />
                  <FieldRow
                    label="Vigencia inicio"
                    numeric
                    {...renderInt(ineFront.vigencia_inicio)}
                  />
                  <FieldRow
                    label="Vigencia fin"
                    numeric
                    {...renderInt(ineFront.vigencia_fin)}
                  />
                  <FieldRow
                    label="Documento presente"
                    {...renderBool(ineFront.document_present)}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No disponible.
                </Typography>
              )}
            </DocGroup>

            <Divider />

            <DocGroup title="INE (reverso)">
              {ineBack ? (
                <>
                  <FieldRow label="CIC" numeric {...renderTextField(ineBack.cic)} />
                  <FieldRow
                    label="MRZ leída"
                    text={mrzLines > 0 ? `Sí (${mrzLines} líneas)` : "No"}
                    muted={mrzLines === 0}
                  />
                  <FieldRow
                    label="Documento presente"
                    {...renderBool(ineBack.document_present)}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Reverso no extraído.
                </Typography>
              )}
            </DocGroup>

            <Divider />

            <DocGroup title="Certificado">
              {cert ? (
                <>
                  <FieldRow label="CURP" {...renderTextField(cert.curp)} />
                  <FieldRow label="Folio" {...renderTextField(cert.folio)} />
                  <FieldRow label="Nombre" {...renderTextField(cert.nombre)} />
                  <FieldRow
                    label="Institución"
                    {...renderTextField(cert.institucion)}
                  />
                  <FieldRow
                    label="Año de emisión"
                    numeric
                    {...renderInt(cert.anio_emision)}
                  />
                  <FieldRow
                    label="Tipo"
                    text={typeof cert.cert_type === "string" ? (CERT_TYPE_LABEL[cert.cert_type] ?? cert.cert_type) : "no leído"}
                    muted={typeof cert.cert_type !== "string" || cert.cert_type === ""}
                  />
                  <FieldRow label="Tiene QR" {...renderBool(cert.has_qr)} />
                  <FieldRow
                    label="Contenido QR"
                    {...renderString(cert.qr_payload)}
                  />
                  <FieldRow
                    label="Sello SEP presente"
                    {...renderBool(cert.sep_seal_present)}
                  />
                  <FieldRow label="Legible" {...renderBool(cert.legible)} />
                  {conformidadRows(cert).map((row) => (
                    <FieldRow key={row.label} {...row} />
                  ))}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No disponible.
                </Typography>
              )}
            </DocGroup>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
